import type { ReactNode, RefObject } from 'react';
import type { ShopifyGlance } from '@discovr/types';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { WarningCircle } from '../design-system/icons';
import { REDUCED_MOTION_DIALOG_CLASS } from '../components/ui/modalScrim';
import { ShopifyGlanceRow, type ShopifyGlanceRowProps } from './ShopifyGlanceRow';

export interface ShopifyGlanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  glance?: ShopifyGlance;
  loading?: boolean;
  error?: boolean;
  onRetry: () => void;
  /** Router-free slot for the "View store discounts" button; omitted for roles without access. */
  discountsAction?: ReactNode;
  renderLink: ShopifyGlanceRowProps['renderLink'];
  /** The control that opened this; Radix has no trigger here to restore focus to. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

/** The store's at-a-glance figures, behind an "Overview" button instead of inline on the card. */
export function ShopifyGlanceDialog({
  open,
  onOpenChange,
  glance,
  loading,
  error,
  onRetry,
  discountsAction,
  renderLink,
  returnFocusTo,
}: ShopifyGlanceDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-clarity-mask="True"
        aria-describedby={undefined}
        className={`max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-lg ${REDUCED_MOTION_DIALOG_CLASS}`}
        onCloseAutoFocus={(event) => {
          if (!returnFocusTo?.current) return;
          event.preventDefault();
          returnFocusTo.current.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Shopify overview</DialogTitle>
        </DialogHeader>
        {error ? (
          <Alert variant="destructive" role="alert" data-testid="shopify-glance-error">
            <WarningCircle aria-hidden="true" />
            <span>Couldn't load the store figures. Try again shortly.</span>
            <Button
              variant="outline"
              size="sm"
              className="col-start-2 mt-2 justify-self-start"
              onClick={onRetry}
            >
              Try again
            </Button>
          </Alert>
        ) : (
          <ShopifyGlanceRow glance={glance} loading={loading} renderLink={renderLink} />
        )}
        {discountsAction}
      </DialogContent>
    </Dialog>
  );
}
